import React, { useEffect, useRef } from 'react';
import Popup from 'reactjs-popup';
import Logo from './Logo_w_Text_Small.png';
import { Pages } from '../Pages';
import './Navbar.css';

import useActions from '../../hooks/use-actions';
import useTypedSelector from '../../hooks/use-type-selectors';
import { ExportFormat } from '../../state/actions';
import { AudioPlaybackState } from '../../models/AudioPlayback';
import { IProjectSaveFile } from '../../models/ProjectFile';
import { findNextAudioClipStartOrEndTime, findPrevAudioClipStartOrEndTime } from '../../models/Timeline';
import { giveScreenReaderFeedback, timeToScreenReaderText } from '../../utils/screenReader';

interface NavbarProps {
	currentPage: Pages;
	changePage: Function;
	leaveWelcomePage: Function;
	updateOverlay(e: boolean): void;
}

const Navbar: React.FC<NavbarProps> = (props: NavbarProps) => {
	const { exportTimeline, saveProject, loadProject, resetState, deleteAudioClipsAtTime, setShortcutsEnabled } = useActions();
	const audioPlayback = useTypedSelector((state) => state.audioPlayback);
	const settings = useTypedSelector((state) => state.settings);
	const timeline = useTypedSelector((state) => state.undoable.timeline);
	const firstNavBarElement = useRef<HTMLButtonElement>(null);
	
	const { undo, redo, playTimeline, stopPlayback, setTimelineTime, setAllTracksMuted } = useActions();

	const openNewProject = () => {
		resetState();
		props.leaveWelcomePage();
	}

	const onModalOpen = () => {
		setShortcutsEnabled(false);
		props.updateOverlay(true);
	}

	const onModalClose = () => {
		setShortcutsEnabled(true);
		props.updateOverlay(false);
	}

	const handleLoadProject = () => {
		const fileInput = document.getElementById('load-file');
		if (fileInput) {
			fileInput.click();
		}
	};

	const handleOnFileSelected = (event: any) => {
		if (event.target.files.length === 1) {
			const file = event.target.files[0];

			const reader = new FileReader();

			reader.onload = (e) => {
				const result = e.target!.result;
				if (result) {
					const dataObj = JSON.parse(result as string);
					loadProject(dataObj as IProjectSaveFile);
					props.leaveWelcomePage();
				}
			};

			reader.readAsText(file);
		}
	};

	const exportProjectFeedback = () => {
		var fileFormatElement = (document.getElementById("exportFileFormat")) as HTMLSelectElement;
		var sampleRateElement = (document.getElementById("sampleRate")) as HTMLInputElement;
		var fileName = ((document.getElementById("fileName")) as HTMLInputElement).value;
		var fileFormat = Number(fileFormatElement.options[fileFormatElement.selectedIndex].value);
		var sampleRate = Number(sampleRateElement.value);

		exportTimeline(fileName, fileFormat, sampleRate);
		giveScreenReaderFeedback('project exported');
	}

	const changePage = (newPage: Pages) => {
		if (!audioPlayback.projectActive) return;

		props.changePage(newPage);
	}

	useEffect(() => {
		const setTimelineTimeAndGiveFeedback = (newTime: number) => {
			newTime = Math.max(0, Math.min(timeline.duration, newTime));
			setTimelineTime(newTime);
			giveScreenReaderFeedback("Playhead at " + timeToScreenReaderText(newTime));
		}

		const onKeyDown = (event: KeyboardEvent) => {
			if(!settings.shortcutsEnabled) return;

			if ((event.key === 'z' || event.key === 'Z') && event.ctrlKey) {
				if (event.shiftKey) {
					redo();
				} else {
					undo();
				}
			} else if ((event.key === 'y' || event.key === 'Y') && event.ctrlKey) {
				redo();
			} else if (event.key === '`') {
				if (audioPlayback.state === AudioPlaybackState.PlayingTimeline) {
					stopPlayback();
				} else {
					playTimeline(audioPlayback.timelineTime);
				}
			} else if (event.code === 'Digit1' && event.altKey) {
				const timeDelta = 0.01;
				setTimelineTimeAndGiveFeedback(audioPlayback.timelineTime + (event.shiftKey ? -timeDelta : timeDelta));
			} else if (event.code === 'Digit2' && event.altKey) {
				const timeDelta = 0.1;
				setTimelineTimeAndGiveFeedback(audioPlayback.timelineTime + (event.shiftKey ? -timeDelta : timeDelta));
			} else if (event.code === 'Digit3' && event.altKey) {
				const timeDelta = 1;
				setTimelineTimeAndGiveFeedback(audioPlayback.timelineTime + (event.shiftKey ? -timeDelta : timeDelta));
			} else if (event.code === 'Digit4' && event.altKey) {
				const timeDelta = 10;
				setTimelineTimeAndGiveFeedback(audioPlayback.timelineTime + (event.shiftKey ? -timeDelta : timeDelta));
			} else if (event.code === 'Digit5' && event.altKey) {
				const timeDelta = 30;
				setTimelineTimeAndGiveFeedback(audioPlayback.timelineTime + (event.shiftKey ? -timeDelta : timeDelta));
			} else if (event.code === 'Digit6' && event.altKey) {
				const timeDelta = 60;
				setTimelineTimeAndGiveFeedback(audioPlayback.timelineTime + (event.shiftKey ? -timeDelta : timeDelta));
			} else if (event.code === 'Digit7' && event.altKey) {
				const timeDelta = 60 * 5;
				setTimelineTimeAndGiveFeedback(audioPlayback.timelineTime + (event.shiftKey ? -timeDelta : timeDelta));
			} else if (event.code === 'Digit8' && event.altKey) {
				const timeDelta = 60 * 10;
				setTimelineTimeAndGiveFeedback(audioPlayback.timelineTime + (event.shiftKey ? -timeDelta : timeDelta));
			} else if (event.code === 'Digit9' && event.altKey) {
				if (event.shiftKey) {
					const time = findPrevAudioClipStartOrEndTime(timeline, audioPlayback.timelineTime);
					setTimelineTimeAndGiveFeedback(time !== null ? time : 0);
				} else {
					const time = findNextAudioClipStartOrEndTime(timeline, audioPlayback.timelineTime);
					setTimelineTimeAndGiveFeedback(time !== null ? time : timeline.duration);
				}
			} else if (event.code === 'Digit0' && event.altKey) {
				if (event.shiftKey) {
					setTimelineTimeAndGiveFeedback(0);
				} else {
					setTimelineTimeAndGiveFeedback(timeline.duration);
				}
			} else if (event.key === '_') {
				let totalTracks = 3;
				let mutedTracks = 0;
				if (timeline.musicTrack.muted) mutedTracks++;
				if (timeline.ambienceTrack.muted) mutedTracks++;
				if (timeline.foleyTrack.muted) mutedTracks++;

				for (const dancer of timeline.dancers) {
					totalTracks += 5;
					if (dancer.aboveHeadTrack.muted) mutedTracks++;
					if (dancer.headTrack.muted) mutedTracks++;
					if (dancer.torsoTrack.muted) mutedTracks++;
					if (dancer.legsTrack.muted) mutedTracks++;
					if (dancer.groundTrack.muted) mutedTracks++;
				}

				let muteAll = mutedTracks < totalTracks * 0.5;
				setAllTracksMuted(muteAll);
				giveScreenReaderFeedback("All tracks " + (muteAll ? "off" : "on"));
			} else if (event.key === "Backspace" || event.key === "Delete") {
				if (firstNavBarElement.current !== null && document.activeElement?.nodeName !== "INPUT") {
					firstNavBarElement.current.focus();
				}
			} else if (event.code === "BracketRight" && event.altKey) {
				deleteAudioClipsAtTime(audioPlayback.timelineTime);
			}
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [undo, redo, playTimeline, stopPlayback, audioPlayback, timeline, settings, setAllTracksMuted, setTimelineTime, deleteAudioClipsAtTime]);

	return (
		<nav className='navbarContainer' aria-label='Nav Bar'>
			<div style={{ display: 'flex', width: '20%', minWidth: '300px' }}>
				<img src={Logo} alt='Welcome to Doppeltone' width='300px' />
			</div>
			<div className='buttonsContainer'>
				<div className='tabsContainer'>
					<button
						className={props.currentPage === Pages.STAGE ? 'pageTab-Stage selected' : 'pageTab-Stage'}
						disabled={!audioPlayback.projectActive}
						aria-label='Create or modify stage'
						onClick={() => {
							changePage(Pages.STAGE);
						}}
					>
						STAGE
					</button>
					<button
						className={props.currentPage === Pages.DANCERS ? 'pageTab-Dancers selected' : 'pageTab-Dancers'}
						disabled={!audioPlayback.projectActive}
						aria-label='Add or remove dancers and paths'
						onClick={() => {
							changePage(Pages.DANCERS);
						}}
					>
						DANCERS
					</button>
					<button
						ref={firstNavBarElement}
						className={props.currentPage === Pages.AUDIO ? 'pageTab-Audio selected' : 'pageTab-Audio'}
						disabled={!audioPlayback.projectActive}
						aria-label='Browse and import audio'
						onClick={() => {
							changePage(Pages.AUDIO);
						}}
					>
						AUDIO
					</button>
					<button
						className={props.currentPage === Pages.SET_PIECES ? 'pageTab-SetPieces selected' : 'pageTab-SetPieces'}
						disabled={!audioPlayback.projectActive}
						aria-label='Add or edit set pieces'
						onClick={() => {
							changePage(Pages.SET_PIECES);
						}}
					>
						SET PIECES
					</button>
					<button
						className={props.currentPage === Pages.ACOUSTICS ? 'pageTab-Acoustics selected' : 'pageTab-Acoustics'}
						disabled={!audioPlayback.projectActive}
						aria-label='Choose or modify acoustic theme'
						onClick={() => {
							changePage(Pages.ACOUSTICS);
						}}
					>
						ACOUSTICS
					</button>
				</div>
				<div className='projectOptionsContainer'>
					<button 
						className='pageTab-New'
						aria-label='Start new project'
						onClick={openNewProject}
					>
						NEW
					</button>
					<input
						id='load-file'
						type='file'
						accept='.dpl'
						hidden
						onChange={handleOnFileSelected}
					/>
					<button
						className='pageTab-Load'
						aria-label='Load previously saved project'
						onClick={handleLoadProject}
					>
						LOAD
					</button>
					<button 
						className='pageTab-Save' 
						aria-label='Save current project' 
						disabled={!audioPlayback.projectActive}
						onClick={saveProject}
					>
						SAVE
					</button>
					<Popup
						modal
						onOpen={onModalOpen}
						onClose={onModalClose}
						trigger={
							<button
								className='pageTab-Export'
								aria-label='Export project to binaural file'
								disabled={!audioPlayback.projectActive}
								onClick={() => giveScreenReaderFeedback('select export settings')}
							>
								EXPORT
							</button>
						}
					>
						<div className='modalWindow'>
							<div style={{ display: 'flex', justifyContent: 'center' }}>
								<img className='exportIcon' alt='export icon'/>
								<h1 style={{ textAlign: 'center', margin: '0' }}>EXPORT PROJECT</h1>
							</div>
							<hr style={{ margin: '10px 0px 10px 0px' }}/>
							<div style={{ display: 'flex', justifyContent: 'space-around', width: '100%' }}>
								<div style={{ width: '20%' }}>
									<p style={{ margin: '0px', marginBottom: '5px' }}>File name</p>
									<input id='fileName' className='inputBox' style={{ margin: '0', width: '100%' }} aria-label='file name' defaultValue='export' />
								</div>
								<div style={{ width: '20%' }}>
									<p style={{ marginTop: '0px', marginBottom: '5px' }}>File format</p>
									<select id='exportFileFormat' className='dropdown' aria-label='file format'>
										{Object.keys(ExportFormat).filter(format => isNaN(Number(format))).map((format, index) => 
                	                		<option value={index}>{format}</option>
                	            		)}
									</select>
								</div>
								<div style={{ width: '20%' }}>
									<p style={{ margin: '0px', marginBottom: '5px' }}>Sample rate</p>
									<input id='sampleRate' className='inputBox' style={{ margin: '0', width: '100%' }} aria-label='sample rate' min='0' type="number" defaultValue={44100} />
								</div>
								<div style={{ width: '20%', display: 'flex', alignItems: 'flex-end' }}>
									<button aria-label='export project' className='exportProjectButton' onClick={exportProjectFeedback}>Export</button>
								</div>
							</div>
						</div>
					</Popup>
					<button className='pageTab-CVD' aria-label='Colour blind mode' hidden>
						CVD
					</button>
				</div>
			</div>
		</nav>
	);
};

export default Navbar;
